import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate as useRouterNavigate } from 'react-router-dom';
import { McButton, McIcon, McTag, McTextarea } from '@maersk-global/mds-react-wrapper';
import { McProgressIndicator } from '@maersk-global/mds-react-wrapper/components-core/mc-progress-indicator';
import { demandsApi } from '../api/demandsApi';
import { applicationsApi, type ApplicationRepository } from '../api/applicationsApi';
import { LoadingIndicator } from './LoadingIndicator';
import { DemandStatusBadge } from './DemandStatusBadge';
import { LlmLogPanel } from './LlmLogPanel';
import { CostDetailModal } from './CostDetailModal';
import { Breadcrumb } from './Breadcrumb';
import { TaskDescriptionView } from './TaskDescriptionView';
import {
  taskExecutionStatusAppearance,
  formatTaskExecutionStatus,
  formatCost,
} from '../utils/helpers/statusHelpers';
import type { StackType, TaskSummary } from '../types/engineering';

// ─── Local repo type ─────────────────────────────────────────────────────────

interface LocalRepo {
  id?: number;
  key: string;
  repository: string;
  type?: StackType;
}

// ─── Repository Modal ─────────────────────────────────────────────────────────

interface RepoModalProps {
  repos: LocalRepo[];
  onClose: () => void;
  onAdd: (entries: Array<{ repository: string; type: StackType | '' }>) => void;
  onRemove: (key: string) => void;
  appRepositoryOptions: { value: string; label: string }[];
  isFetchingRepos: boolean;
  isErrorRepos: boolean;
  effectiveSystem: string;
  selectedMacid: string;
  isLoadingApps: boolean;
  isErrorApps: boolean;
}

const STACK_OPTIONS = [
  { value: '', label: 'Select type...' },
  { value: 'FRONTEND', label: 'Frontend' },
  { value: 'BACKEND', label: 'Backend' },
];

const RepoModal = ({ repos, onClose, onAdd, onRemove, appRepositoryOptions, isFetchingRepos, isErrorRepos, effectiveSystem, selectedMacid, isLoadingApps, isErrorApps }: RepoModalProps) => {
  // checkedItems: key = repo URL, value = selected type ('' = not yet chosen)
  const [checkedItems, setCheckedItems] = useState<Record<string, StackType | ''>>({}); 
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const checkedCount = Object.keys(checkedItems).length;

  // Add is enabled only when ≥1 repo is checked AND every checked repo has a type
  const canAdd = checkedCount > 0 && Object.values(checkedItems).every((t) => t !== '');

  const toggleRepo = (repoUrl: string) => {
    setCheckedItems((prev) => {
      if (repoUrl in prev) {
        const next = { ...prev };
        delete next[repoUrl];
        return next;
      }
      return { ...prev, [repoUrl]: '' };
    });
  };

  const setType = (repoUrl: string, type: StackType | '') => {
    setCheckedItems((prev) => ({ ...prev, [repoUrl]: type }));
  };

  const handleAdd = () => {
    if (!canAdd) return;
    const entries = Object.entries(checkedItems).map(([repository, type]) => ({ repository, type }));
    onAdd(entries);
    setCheckedItems({});
    setDropdownOpen(false);
  };

  // Filter out repos already added to the demand
  const alreadyAdded = new Set(repos.map((r) => r.repository));
  const availableOptions = appRepositoryOptions.filter((o) => !alreadyAdded.has(o.value));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', width: '100%' }}>
        <div className="modal-panel__header">
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Manage Repositories</h2>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-panel__body">
          {repos.length === 0 ? (
            <p style={{ color: 'var(--mds_brand_appearance_neutral_weak_text-color)', fontSize: '0.875rem' }}>
              No repositories linked yet.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: '0 0 1rem', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {repos.map((r) => (
                <li key={r.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {r.type && (
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px', borderRadius: '4px',
                      background: r.type === 'FRONTEND' ? '#1d4ed8' : '#c2410c', color: '#fff', flexShrink: 0,
                    }}>
                      {r.type}
                    </span>
                  )}
                  <span style={{ flex: 1, fontSize: '0.875rem', wordBreak: 'break-all' }}>{r.repository}</span>
                  <McButton label="Remove" appearance="error" fit="small" variant="outlined" click={() => onRemove(r.key)} />
                </li>
              ))}
            </ul>
          )}
          <div style={{ borderTop: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)', paddingTop: '1rem' }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Add Repository</p>
            {/* paddingBottom when open so the absolute dropdown panel isn't clipped by overflow-y:auto */}
            <div style={{ paddingBottom: dropdownOpen ? '300px' : 0 }}>
              {/* Multi-select dropdown with checkboxes */}
              {effectiveSystem && (
                isLoadingApps ? (
                  <span style={{ color: 'var(--mds_brand_appearance_neutral_weak_text-color)', fontSize: '0.875rem' }}>Loading applications...</span>
                ) : isErrorApps ? (
                  <span style={{ color: 'var(--mds_brand_appearance_error_default_text-color)', fontSize: '0.875rem' }}>Failed to load applications.</span>
                ) : !selectedMacid ? null : isFetchingRepos ? (
                  <span style={{ color: 'var(--mds_brand_appearance_neutral_weak_text-color)', fontSize: '0.875rem' }}>Loading repositories...</span>
                ) : isErrorRepos ? (
                  <span style={{ color: 'var(--mds_brand_appearance_error_default_text-color)', fontSize: '0.875rem' }}>Failed to load repositories.</span>
                ) : availableOptions.length > 0 ? (
                  <div style={{ position: 'relative' }}>
                    {/* Trigger button */}
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((o) => !o)}
                      style={{
                        width: '100%', padding: '6px 10px', fontSize: '0.875rem', borderRadius: '4px',
                        border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #ccc)',
                        background: 'transparent', color: 'inherit', cursor: 'pointer',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span>
                        {checkedCount > 0
                          ? `${checkedCount} repositor${checkedCount > 1 ? 'ies' : 'y'} selected`
                          : 'Select repositories...'}
                      </span>
                      <span style={{ fontSize: '0.7rem', marginLeft: '0.5rem' }}>{dropdownOpen ? '▲' : '▼'}</span>
                    </button>

                    {/* Dropdown panel */}
                    {dropdownOpen && (
                      <div style={{
                        position: 'absolute', zIndex: 100, width: '100%', top: 'calc(100% + 2px)',
                        border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #ccc)',
                        borderRadius: '4px',
                        background: 'var(--mds_brand_appearance_neutral_default_background-color, #fff)',
                        maxHeight: '260px', overflowY: 'auto',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      }}>
                        {availableOptions.map((o) => {
                          const isChecked = o.value in checkedItems;
                          return (
                            <div
                              key={o.value}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                padding: '8px 10px',
                                borderBottom: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #eee)',
                                background: isChecked
                                  ? 'var(--mds_brand_appearance_neutral_weak_background-color, #f0f2f5)'
                                  : 'transparent',
                              }}
                            >
                              {/* Checkbox */}
                              <input
                                type="checkbox"
                                id={`repo-check-${o.value}`}
                                checked={isChecked}
                                onChange={() => toggleRepo(o.value)}
                                style={{ cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                              />
                              {/* Repo name */}
                              <label
                                htmlFor={`repo-check-${o.value}`}
                                style={{
                                  flex: 1, fontSize: '0.875rem', cursor: 'pointer',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}
                              >
                                {o.label}
                              </label>
                              {/* Type selector — only shown when repo is checked */}
                              {isChecked && (
                                <select
                                  value={checkedItems[o.value]}
                                  onChange={(e) => { e.stopPropagation(); setType(o.value, e.target.value as StackType | ''); }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    padding: '3px 6px', fontSize: '0.8rem', borderRadius: '4px',
                                    border: checkedItems[o.value] === ''
                                      ? '1px solid var(--mds_brand_appearance_error_default_text-color, #dc2626)'
                                      : '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #ccc)',
                                    background: 'transparent', color: 'inherit', flexShrink: 0,
                                  }}
                                >
                                  {STACK_OPTIONS.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null
              )}
            </div>
          </div>
        </div>

        {/* ── Footer — always visible, never scrolled away ───────────────── */}
        <div style={{
          flexShrink: 0,
          padding: '0.75rem 1.25rem',
          borderTop: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #e0e0e0)',
          display: 'flex', flexDirection: 'column', gap: '0.5rem',
        }}>
          {checkedCount > 0 && !canAdd && (
            <span style={{ fontSize: '0.8rem', color: 'var(--mds_brand_appearance_error_default_text-color, #dc2626)' }}>
              Please select a type for all checked repositories.
            </span>
          )}
          <McButton
            label="Add"
            appearance="primary"
            fit="small"
            disabled={!canAdd}
            click={handleAdd}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Stack class helper ───────────────────────────────────────────────────────

const stackClass = (stack?: StackType): string => {
  if (stack === 'FRONTEND') return 'task-card--frontend';
  if (stack === 'BACKEND') return 'task-card--backend';
  return '';
};

// ─── DemandDetailContent ──────────────────────────────────────────────────────

export interface DemandDetailContentProps {
  id: string;
  /** Called when a task card is clicked. If omitted the component navigates to /tasks/:id. */
  onNavigateToTask?: (taskId: string) => void;
  /** When true, hides the breadcrumb entirely (e.g. when shown inside a modal with its own close button). */
  hideBack?: boolean;
}

export const DemandDetailContent = ({ id, onNavigateToTask, hideBack = false }: DemandDetailContentProps) => {
  const navigate = useRouterNavigate();
  const queryClient = useQueryClient();

  const [showRepoModal, setShowRepoModal] = useState(false);
  const [showCostDetail, setShowCostDetail] = useState(false);
  const [sendingToPlanning, setSendingToPlanning] = useState(false);
  const [retryingArchitect, setRetryingArchitect] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showArchitectLogs, setShowArchitectLogs] = useState(false);
  const [shouldFetchRepos, setShouldFetchRepos] = useState(false);

  // Draft state — persisted on "Save" or "Send to Planning"
  const [systemDraft, setSystemDraft] = useState<string | undefined>(undefined);
  const [selectedMacid, setSelectedMacid] = useState<string>('');
  const [descriptionDraft, setDescriptionDraft] = useState<string | undefined>(undefined);
  const [editingDescription, setEditingDescription] = useState(false);
  const [showSystemSuggestions, setShowSystemSuggestions] = useState(false);
  const [pendingAddRepos, setPendingAddRepos] = useState<LocalRepo[]>([]);
  const [deletedRepoIds, setDeletedRepoIds] = useState<number[]>([]);
  const repoKeyCounter = useRef(0);

  const { data: demand, isLoading, isError } = useQuery({
    queryKey: ['demands', id],
    queryFn: () => demandsApi.getById(id),
    enabled: !!id,
  });

  const { data: applications = [], isLoading: isLoadingApps, isError: isErrorApps } = useQuery({
    queryKey: ['applications'],
    queryFn: () => applicationsApi.getApplications(),
    retry: 1,
  });

  const systemOptions = useMemo(() => {
    const base = applications.map((a) => ({ value: a.name, label: a.name, macId: a.macId }));
    const currentSystem = demand?.systemName;
    if (currentSystem && !base.find((o) => o.value === currentSystem)) {
      base.unshift({ value: currentSystem, label: currentSystem, macId: '' });
    }
    return base;
  }, [applications, demand?.systemName]);

  const { data: appRepositories = [], isFetching: isFetchingRepos, isError: isErrorRepos } = useQuery<ApplicationRepository[]>({
    queryKey: ['app-repositories', selectedMacid],
    queryFn: () => applicationsApi.getRepositoriesByMacid(selectedMacid),
    enabled: !!selectedMacid && shouldFetchRepos,
    retry: 1,
  });

  // Auto-initialize selectedMacid when demand and applications are both loaded
  useEffect(() => {
    console.log('[DemandDetail] useEffect triggered — systemName:', demand?.systemName, '| applications count:', applications.length, '| isErrorApps:', isErrorApps);
    if (!demand?.systemName) return;
    if (applications.length === 0) return;
    const match = applications.find(
      (a) => a.name.trim().toLowerCase() === demand.systemName.trim().toLowerCase()
    );
    console.log('[DemandDetail] match found:', match ?? 'NONE');
    setSelectedMacid(match ? match.macId : '');
  }, [demand?.systemName, applications, isErrorApps]);

  const appRepositoryOptions = useMemo(() =>
    appRepositories.map((r) => ({ value: r.link, label: r.name })),
    [appRepositories],
  );

  // Effective values — draft takes priority over server data
  const effectiveSystem = systemDraft !== undefined ? systemDraft : (demand?.systemName ?? '');
  const effectiveDescription = descriptionDraft !== undefined ? descriptionDraft : (demand?.description ?? '');

  // Dirty flag — true when any local edit has not yet been saved
  const isDirty =
    systemDraft !== undefined ||
    descriptionDraft !== undefined ||
    pendingAddRepos.length > 0 ||
    deletedRepoIds.length > 0;
  const effectiveRepos = useMemo(() => {
    const existing = (demand?.repositories ?? [])
      .filter((r) => !deletedRepoIds.includes(r.id))
      .map((r) => ({ id: r.id, key: String(r.id), repository: r.repository, type: r.type }));
    return [...existing, ...pendingAddRepos];
  }, [demand?.repositories, deletedRepoIds, pendingAddRepos]);

  const addRepo = (entries: Array<{ repository: string; type: StackType | '' }>) => {
    setPendingAddRepos((prev) => {
      const newEntries = entries.map((e) => {
        repoKeyCounter.current += 1;
        return { key: `new-${repoKeyCounter.current}`, repository: e.repository, type: e.type || undefined };
      });
      return [...prev, ...newEntries];
    });
  };

  const removeRepo = (key: string) => {
    if (key.startsWith('new-')) {
      setPendingAddRepos((prev) => prev.filter((r) => r.key !== key));
    } else {
      setDeletedRepoIds((prev) => [...prev, parseInt(key, 10)]);
    }
  };

  const persistEdits = async () => {
    await demandsApi.update(id, {
      systemName: effectiveSystem.trim(),
      macId: selectedMacid || undefined,
      description: effectiveDescription.trim(),
    });
    await Promise.all(deletedRepoIds.map((rid) => demandsApi.deleteRepository(id, rid)));
    await Promise.all(
      pendingAddRepos.map((r) =>
        demandsApi.addRepository(id, { repository: r.repository, type: r.type || undefined }),
      ),
    );
    // Invalidate both detail and list queries to update everywhere
    await queryClient.invalidateQueries({ queryKey: ['demands', id] });
    await queryClient.invalidateQueries({ queryKey: ['demands'] });
    // Reset drafts
    setSystemDraft(undefined);
    setDescriptionDraft(undefined);
    setPendingAddRepos([]);
    setDeletedRepoIds([]);
    setEditingDescription(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistEdits();
    } finally {
      setSaving(false);
    }
  };

  const handleSendToPlanning = async () => {
    setSendingToPlanning(true);
    try {
      await persistEdits();
      await demandsApi.sendToPlanning(id);
      // Invalidate all demand queries to reflect status change in lists
      await queryClient.invalidateQueries({ queryKey: ['demands'] });
    } finally {
      setSendingToPlanning(false);
    }
  };

  const handleRetryArchitect = async () => {
    setRetryingArchitect(true);
    try {
      await demandsApi.retryArchitect(id);
      // Invalidate all demand queries to reflect status change
      await queryClient.invalidateQueries({ queryKey: ['demands'] });
    } finally {
      setRetryingArchitect(false);
    }
  };

  const handleTaskClick = (taskId: string) => {
    if (onNavigateToTask) {
      onNavigateToTask(taskId);
    } else {
      navigate(`/tasks/${taskId}`);
    }
  };

  if (isLoading) return <LoadingIndicator />;

  if (isError || !demand) {
    return (
      <div>
        {!hideBack && (
          <Breadcrumb
            items={[
              { label: 'Demands', path: '/demands' },
              { label: 'Demand Detail' }
            ]}
          />
        )}
        <div className="empty-state">Demand not found.</div>
      </div>
    );
  }

  const insertDateFormatted = demand.insertDate
    ? new Date(demand.insertDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  // Use backend-calculated weighted percentage
  const percentConcluded = demand.percentConcluded ?? 0;

  const hasDescription = !!(effectiveDescription && effectiveDescription.trim());
  const hasSystem = !!(effectiveSystem && effectiveSystem.trim() && effectiveSystem.toUpperCase() !== 'UNDEFINED');
  const hasRepo = effectiveRepos.length > 0;
  const canSendToPlanning = hasDescription && hasSystem && hasRepo;

  return (
    <div>
      {/* Breadcrumb navigation */}
      {!hideBack && (
        <Breadcrumb
          items={[
            { label: 'Demands', path: '/demands' },
            { label: 'Demand Detail' }
          ]}
        />
      )}

      {/* Top row: action buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {isDirty && (
            <McButton
              label="Save"
              appearance="neutral"
              fit="small"
              loading={saving}
              click={handleSave}
            />
          )}
          {demand.status === 'BACKLOG' && (
            <McButton
              label="Send to Planning"
              appearance="primary"
              fit="small"
              disabled={!canSendToPlanning}
              loading={sendingToPlanning}
              title={
                !hasDescription ? 'Description is required' :
                !hasSystem ? 'System must be defined' :
                !hasRepo ? 'At least one repository must be linked' :
                'Send demand to Planning'
              }
              click={handleSendToPlanning}
            />
          )}
          {demand.status === 'PLANNING_ERROR' && (
            <McButton
              label="Retry Architect"
              appearance="primary"
              fit="small"
              loading={retryingArchitect}
              title="Retry the Architect AI to break down the demand again"
              click={handleRetryArchitect}
            />
          )}
        </div>
      </div>

      {/* Page header */}
      <div className="demand-detail-header">
        <div className="demand-detail-header__meta">
          <span className="demand-detail-header__code">{demand.code}</span>
          <DemandStatusBadge status={demand.status} />
          {demand.cost != null && (
            <span className="demand-detail-header__cost" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              {formatCost(demand.cost)}
              <button
                aria-label="Cost details"
                title="Cost details"
                onClick={() => setShowCostDetail(true)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                  color: 'inherit', display: 'inline-flex', alignItems: 'center', opacity: 0.7,
                }}
              >
                <McIcon icon="info-circle" size="16" />
              </button>
            </span>
          )}
        </div>
        <h1 className="demand-detail-header__title" style={{ fontSize: '1.25rem' }}>{demand.title}</h1>
      </div>

      {/* Two-column body */}
      <div className="demand-detail-body">
        <div className="demand-detail-body__left">
          <div className="detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h2 className="detail-section__title" style={{ margin: 0 }}>Description</h2>
              {!editingDescription && (
                <McButton
                  label="Edit"
                  hiddenlabel
                  icon="pencil"
                  appearance="neutral"
                  variant="plain"
                  fit="small"
                  click={() => setEditingDescription(true)}
                />
              )}
            </div>
            {editingDescription ? (
              <McTextarea
                value={effectiveDescription}
                rows={6}
                width="full"
                placeholder="Describe the demand..."
                input={(e: Event) => setDescriptionDraft((e.target as HTMLTextAreaElement).value)}
              />
            ) : (
              effectiveDescription
                ? TaskDescriptionView(effectiveDescription)
                : <span style={{ color: 'var(--mds_brand_appearance_neutral_weak_text-color)', fontSize: '0.875rem' }}>No description.</span>
            )}
          </div>

          <div className="detail-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <h2 className="detail-section__title" style={{ margin: 0 }}>Tasks ({demand.tasks?.length ?? 0})</h2>
              <McButton label="Architect Logs" appearance="neutral" fit="small" click={() => setShowArchitectLogs((v) => !v)}>
                <McIcon icon="robot" slot="icon" />
              </McButton>
            </div>
            {showArchitectLogs && (
              <LlmLogPanel
                executionId={id}
                agent="ARCHITECT"
                onDone={() => queryClient.invalidateQueries({ queryKey: ['demands', id] })}
              />
            )}
            <div className="demand-detail-progress">
              <McProgressIndicator
                variant="bar"
                value={percentConcluded}
                max={100}
                label={`${percentConcluded}%`}
                arialabel={`${percentConcluded} percent concluded`}
                fit="small"
                appearance="primary"
              />
            </div>

            {!demand.tasks || demand.tasks.length === 0 ? (
              <div className="empty-state">No tasks yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {demand.tasks.map((task: TaskSummary) => (
                  <div
                    key={task.id}
                    className={`task-card ${stackClass(task.stack)}`}
                    onClick={() => handleTaskClick(task.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTaskClick(task.id); }}
                  >
                    <div className="task-card__left">
                      <div className="task-card__code">{task.code}</div>
                      <div className="task-card__title">{task.title}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', flexShrink: 0, alignItems: 'stretch', minHeight: '48px' }}>
                      <div style={{ 
                        width: '1px', 
                        backgroundColor: '#d1d5db',
                        alignSelf: 'stretch'
                      }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <div style={{ 
                            width: '18px', 
                            height: '18px', 
                            borderRadius: '50%', 
                            backgroundColor: task.stack === 'BACKEND' ? '#ff6b35' : '#0066cc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <path d="M12 6v6l4 2"/>
                            </svg>
                          </div>
                          <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--mds_brand_appearance_neutral_default_text-color)', whiteSpace: 'nowrap' }}>
                            Progress
                          </span>
                        </div>
                        <span style={{ 
                          fontSize: '1rem', 
                          fontWeight: 700, 
                          color: task.stack === 'BACKEND' ? '#ff6b35' : '#0066cc',
                          paddingLeft: '1.5rem'
                        }}>
                          {task.percentCompleted}%
                        </span>
                      </div>
                      {task.cost != null && (
                        <>
                          <div style={{ 
                            width: '1px', 
                            backgroundColor: '#d1d5db',
                            alignSelf: 'stretch'
                          }} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', justifyContent: 'center', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                              <div style={{ 
                                width: '18px', 
                                height: '18px', 
                                borderRadius: '50%', 
                                backgroundColor: '#10b981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: 'white', lineHeight: 1 }}>$</span>
                              </div>
                              <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--mds_brand_appearance_neutral_default_text-color)', whiteSpace: 'nowrap' }}>
                                Cost
                              </span>
                            </div>
                            <span style={{ 
                              fontSize: '1rem', 
                              fontWeight: 600, 
                              color: 'var(--mds_brand_appearance_neutral_default_text-color)'
                            }}>
                              {formatCost(task.cost)}
                            </span>
                          </div>
                          <div style={{ 
                            width: '1px', 
                            backgroundColor: '#d1d5db',
                            alignSelf: 'stretch'
                          }} />
                        </>
                      )}
                      <McTag
                        label={formatTaskExecutionStatus(task.executionStatus)}
                        appearance={taskExecutionStatusAppearance(task.executionStatus)}
                        fit="small"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT column */}
        <div className="demand-detail-body__right">
          <div className="detail-section">
            <h2 className="detail-section__title">Details</h2>
            <div className="detail-info-grid">
              <div>
                <div className="detail-info-item__label">Creator</div>
                <div className="detail-info-item__value">{demand.creator || '—'}</div>
              </div>

              {/* System — always-visible combobox for BACKLOG, read-only otherwise */}
              <div>
                <div className="detail-info-item__label">System</div>
                <div className="detail-info-item__value">
                  <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={effectiveSystem}
                        placeholder="Type or pick a system..."
                        onChange={(e) => { setSystemDraft(e.target.value); setShowSystemSuggestions(true); }}
                        onFocus={() => setShowSystemSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSystemSuggestions(false), 150)}
                        style={{
                          width: '100%', padding: '4px 8px', fontSize: '0.875rem',
                          border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #ccc)',
                          borderRadius: '4px', background: 'transparent', color: 'inherit',
                          boxSizing: 'border-box',
                        }}
                      />
                      {showSystemSuggestions && systemOptions.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                          border: '1px solid var(--mds_brand_appearance_neutral_weak_border-color, #ccc)',
                          borderRadius: '4px', marginTop: '2px',
                          background: 'var(--mds_brand_appearance_neutral_default_background-color, #fff)',
                          maxHeight: '180px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        }}>
                          {systemOptions
                            .filter((o) => o.value.toLowerCase().includes(effectiveSystem.toLowerCase()))
                            .map((o) => (
                              <div
                                key={o.value}
                                onMouseDown={() => { setSystemDraft(o.value); setSelectedMacid((o as { macId?: string }).macId ?? ''); setShowSystemSuggestions(false); }}
                                style={{
                                  padding: '6px 10px', fontSize: '0.875rem', cursor: 'pointer',
                                  color: 'inherit',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--mds_brand_appearance_neutral_weak_background-color, #f5f5f5)')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                              >
                                {o.value}
                              </div>
                            ))}
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {/* Repositories */}
              <div>
                <div className="detail-info-item__label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Repositories</span>
                  <McButton
                    label={effectiveRepos.length === 0 ? 'Add' : 'Edit'}
                    hiddenlabel
                    icon={effectiveRepos.length === 0 ? 'plus' : 'pencil'}
                    appearance="neutral"
                    variant="plain"
                    fit="small"
                    click={() => {
                      setShouldFetchRepos(true);
                      setShowRepoModal(true);
                    }}
                  />
                </div>
                <div className="detail-info-item__value">
                  {effectiveRepos.length === 0 ? (
                    <span style={{ color: 'var(--mds_brand_appearance_neutral_weak_text-color)' }}>None linked</span>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {effectiveRepos.map((r) => (
                        <div key={r.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.375rem' }}>
                          {r.type && (
                            <span style={{
                              fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: '3px',
                              background: r.type === 'FRONTEND' ? '#1d4ed8' : '#c2410c', color: '#fff',
                              whiteSpace: 'nowrap', flexShrink: 0, marginTop: '1px',
                            }}>
                              {r.type}
                            </span>
                          )}
                          <a
                            href={r.repository}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '0.8rem', wordBreak: 'break-all', color: 'var(--mds_brand_appearance_primary_default_text-color)' }}
                          >
                            {r.repository.split('/').pop() || r.repository}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="detail-info-item__label">Created at</div>
                <div className="detail-info-item__value">{insertDateFormatted}</div>
              </div>
              {demand.newRepositoryBranch && (
                <div>
                  <div className="detail-info-item__label">Branch</div>
                  <div className="detail-info-item__value">{demand.newRepositoryBranch}</div>
                </div>
              )}
              {demand.externalLink && (
                <div>
                  <div className="detail-info-item__label">External Link</div>
                  <div className="detail-info-item__value">
                    <a href={demand.externalLink} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--mds_brand_appearance_primary_default_text-color)' }}>
                      {demand.externalLink}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showRepoModal && (
        <RepoModal
          repos={effectiveRepos}
          onClose={() => setShowRepoModal(false)}
          onAdd={addRepo}
          onRemove={removeRepo}
          appRepositoryOptions={appRepositoryOptions}
          isFetchingRepos={isFetchingRepos}
          isErrorRepos={isErrorRepos}
          effectiveSystem={effectiveSystem}
          selectedMacid={selectedMacid}
          isLoadingApps={isLoadingApps}
          isErrorApps={isErrorApps}
        />
      )}

      {showCostDetail && (
        <CostDetailModal
          demandId={id}
          onClose={() => setShowCostDetail(false)}
        />
      )}
    </div>
  );
};
